import type { Position } from '@game/types';
import { TABLE_LENGTH, TABLE_WIDTH } from './PhysicsConfig';

/**
 * Ball rack positions in triangular formation (pool/billiards style)
 * 
 * Standard 15-ball rack layout:
 * 
 *         1
 *       2   3
 *     4   5   6
 *   7   8   9  10
 * 11  12  13  14  15
 * 
 * Positions are in meters relative to center of rack
 * X-axis: left (-) to right (+)
 * Y-axis: front/apex (0) to back (+)
 */

export interface BallLayoutPosition {
  ballNumber: number;
  position: { x: number; y: number }; // Physics world coordinates (meters)
  gridPosition: Position; // Grid position for reference
}

// Ball diameter for spacing (touching balls in triangle)
const BALL_DIAMETER = 0.172; // meters (172mm, 3x larger for visibility)
const ROW_SPACING = BALL_DIAMETER * 0.866; // Vertical spacing for equilateral triangle

/**
 * Standard 15-ball triangular rack
 * Apex at front (Y=0), back row at Y=~34cm
 */
export const BALL_RACK_LAYOUT: BallLayoutPosition[] = [
  // Front row - apex (1 ball)
  { ballNumber: 1, position: { x: 0, y: 0 }, gridPosition: { row: 0, col: 0 } },
  
  // Second row (2 balls)
  { ballNumber: 2, position: { x: -BALL_DIAMETER * 0.5, y: ROW_SPACING }, gridPosition: { row: 1, col: 0 } },
  { ballNumber: 3, position: { x: BALL_DIAMETER * 0.5, y: ROW_SPACING }, gridPosition: { row: 1, col: 1 } },
  
  // Third row (3 balls)
  { ballNumber: 4, position: { x: -BALL_DIAMETER, y: ROW_SPACING * 2 }, gridPosition: { row: 2, col: 0 } },
  { ballNumber: 5, position: { x: 0, y: ROW_SPACING * 2 }, gridPosition: { row: 2, col: 1 } },
  { ballNumber: 6, position: { x: BALL_DIAMETER, y: ROW_SPACING * 2 }, gridPosition: { row: 2, col: 2 } },
  
  // Fourth row (4 balls)
  { ballNumber: 7, position: { x: -BALL_DIAMETER * 1.5, y: ROW_SPACING * 3 }, gridPosition: { row: 3, col: 0 } },
  { ballNumber: 8, position: { x: -BALL_DIAMETER * 0.5, y: ROW_SPACING * 3 }, gridPosition: { row: 3, col: 1 } },
  { ballNumber: 9, position: { x: BALL_DIAMETER * 0.5, y: ROW_SPACING * 3 }, gridPosition: { row: 3, col: 2 } },
  { ballNumber: 10, position: { x: BALL_DIAMETER * 1.5, y: ROW_SPACING * 3 }, gridPosition: { row: 3, col: 3 } },
  
  // Back row (5 balls)
  { ballNumber: 11, position: { x: -BALL_DIAMETER * 2, y: ROW_SPACING * 4 }, gridPosition: { row: 4, col: 0 } },
  { ballNumber: 12, position: { x: -BALL_DIAMETER, y: ROW_SPACING * 4 }, gridPosition: { row: 4, col: 1 } },
  { ballNumber: 13, position: { x: 0, y: ROW_SPACING * 4 }, gridPosition: { row: 4, col: 2 } },
  { ballNumber: 14, position: { x: BALL_DIAMETER, y: ROW_SPACING * 4 }, gridPosition: { row: 4, col: 3 } },
  { ballNumber: 15, position: { x: BALL_DIAMETER * 2, y: ROW_SPACING * 4 }, gridPosition: { row: 4, col: 4 } },
];

/**
 * Get ball position by ball number
 */
export function getBallPosition(ballNumber: number): BallLayoutPosition | undefined {
  return BALL_RACK_LAYOUT.find(b => b.ballNumber === ballNumber);
}

/**
 * Get all ball positions as array
 */
export function getAllBallPositions(): BallLayoutPosition[] {
  return [...BALL_RACK_LAYOUT];
}

/**
 * Position of the rack center on the table
 * Placed at side pocket line (equal spacing from corners)
 */
export const RACK_CENTER = {
  x: 0, // Centered horizontally
  y: TABLE_LENGTH - TABLE_WIDTH / 2, // At side pocket line for equal spacing
};
